import { WizardView, Gap, P } from '@salecycle/ui-component-library';
import Spinner from 'core/ui/components/spinner';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router';
import { State } from 'store';
import { IBrands, IClients, IGroups, ISites, IUserData, Level } from '../../../entities';
import { useNewUserDispatch, useNewUserState } from '../context/useNewUserContext';
import { getSteps, stepRoutes } from '../data/NewUserConstants';
import { arePreviousStepsValid, buildNewUserPath, redirectToLastValidStep } from '../../new-user/utils/helpers';
import AccessLevelStep from './steps/AccessLevelStep';
import AddUserStep from './steps/AddUserStep';
import { SetUserRoleStep } from './steps/SetUserRoleStep/SetUserRoleStep';
import SummaryModal from './steps/SummaryStep/SummaryModal';
import SummaryStep from './steps/SummaryStep/SummaryStep';
import { useRoles } from '../../../data/RolesApi';
import { createUserAction } from 'sections/user-management/actions/createUserAction';
import { UnsavedChangesModal } from 'core/modals/UnsavedChangesModal';
import { NewUserSuccessPage } from './NewUserSuccessPage';

interface IStateProps {
  loadingClientsData: boolean;
  clients: IClients;
  currentStep?: number;
  pathname?: string;
}

interface DispatchProps {
  push: (href: string) => void;
}

export type IProps = IStateProps & DispatchProps;

/* eslint-disable react-hooks/exhaustive-deps */
export const NewUserWizard: React.FC<IProps> = ({
  loadingClientsData,
  clients,
  currentStep,
  pathname,
  push,
}: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [summaryModalIsOpen, setSummaryModalIsOpen] = useState(false);
  const [unsavedChangesModalIsOpen, setUnsavedChangesModalIsOpen] = useState(false);
  const { user, success, error, errorMessage, stepValid, validSteps } = useNewUserState();
  const dispatch = useNewUserDispatch();

  const steps = getSteps(t);

  useEffect(() => () => dispatch({ type: 'RESET_WIZARD' }), []);

  const { rolesData, isRolesLoading } = useRoles();
  useEffect(() => dispatch({ type: 'UPDATE_ROLES', payload: { roles: rolesData } }), [dispatch, rolesData]);

  useEffect(() => {
    const currentPath = pathname.replace(buildNewUserPath(''), '');
    let validPath = false;

    steps.forEach(step => {
      if (step.route === currentPath) {
        validPath = true;
      }
    });

    if (!validPath) {
      push(buildNewUserPath(steps[0].route));
    }
  }, [pathname]);

  useEffect(() => {
    const prevStepsValid = arePreviousStepsValid(validSteps, currentStep);

    if (!prevStepsValid) {
      redirectToLastValidStep(steps, validSteps, push);
    }
  }, [currentStep]);

  useEffect(() => {
    if (error) {
      setSummaryModalIsOpen(false);
    }
  }, [error]);

  const exitWizard = () => {
    setUnsavedChangesModalIsOpen(false);
    history.push('/user-management');
  };

  const onExitButtonClicked = () => {
    setUnsavedChangesModalIsOpen(true);
  };

  const onPrevButtonClicked = () => {
    if (currentStep === 1) {
      setUnsavedChangesModalIsOpen(true);
    } else {
      push(buildNewUserPath(steps[currentStep - 2].route));
    }
  };

  const onNextButtonClicked = () => {
    if (currentStep !== steps.length) {
      push(buildNewUserPath(steps[currentStep].route));
    } else {
      setSummaryModalIsOpen(true);
    }
  };

  const closeSummaryModal = () => {
    setSummaryModalIsOpen(false);
  };

  const handleSummaryModalSubmit = () => {
    const accessLevel = user.selectedAccessLevel.value;
    const role = user.selectedRole.value;
    let groupIds = [];
    let brandIds = [];
    let siteIds = [];

    switch (accessLevel) {
      case 'Brand': {
        brandIds = user.selectedBrand ? [user.selectedBrand.value] : [];
        break;
      }
      case 'Site': {
        siteIds = user.selectedSite ? [user.selectedSite.value] : [];
        break;
      }
      case 'MultiSite': {
        siteIds = user.selectedMultiSites ? user.selectedMultiSites.map(s => s.value) : [];
        groupIds = user.selectedMultiSiteGroup ? [user.selectedMultiSiteGroup.value] : [];
      }
    }

    const newUser: IUserData = {
      email: user.email,
      name: user.name,
      enabled: true,
      accessLevel: user.selectedAccessLevel.value as Level,
      role,
      brandIds,
      siteIds,
      groupIds,
    };
    createUserAction(newUser, dispatch);
  };

  if (loadingClientsData || isRolesLoading) {
    return <Spinner />;
  }

  if (success) {
    return <NewUserSuccessPage />;
  } else {
    return (
      <WizardView
        currentStep={currentStep}
        steps={steps.map(step => {
          return { label: step.label };
        })}
        onExitButtonClicked={currentStep !== 1 ? onExitButtonClicked : null}
        onPrevButtonClicked={onPrevButtonClicked}
        onNextButtonClicked={onNextButtonClicked}
        exitButtonLabel={t('newUser.wizard.cancel')}
        prevButtonLabel={currentStep === 1 ? t('newUser.wizard.exit') : t('newUser.wizard.back')}
        nextButtonLabel={currentStep !== steps.length ? t('newUser.wizard.next') : t('newUser.wizard.confirm')}
        nextButtonDisabled={!stepValid}
      >
        <UnsavedChangesModal
          heading={t('newUser.unsavedChangesModal.heading')}
          isOpen={unsavedChangesModalIsOpen}
          onOkButtonClicked={exitWizard}
          onCancelButtonClicked={() => setUnsavedChangesModalIsOpen(false)}
        >
          <P>{t('newUser.unsavedChangesModal.text1')}</P>
          <Gap top={10} />
          <P>{t('newUser.unsavedChangesModal.text2')}</P>
        </UnsavedChangesModal>
        <Switch>
          <Route
            exact={true}
            path={`/user-management/users/new-user${steps[0].route}`}
            render={() => currentStep === 1 && <AddUserStep currentStep={currentStep} />}
          />
          <Route
            exact={true}
            path={`/user-management/users/new-user${steps[1].route}`}
            render={() =>
              currentStep === 2 &&
              arePreviousStepsValid(validSteps, currentStep) && (
                <AccessLevelStep currentStep={currentStep} clients={clients} />
              )
            }
          />
          <Route
            exact={true}
            path={`/user-management/users/new-user${steps[2].route}`}
            render={() =>
              currentStep === 3 &&
              arePreviousStepsValid(validSteps, currentStep) && <SetUserRoleStep currentStep={currentStep} />
            }
          />
          <Route
            exact={true}
            path={`/user-management/users/new-user${steps[3].route}`}
            render={() =>
              currentStep === 4 &&
              arePreviousStepsValid(validSteps, currentStep) && (
                <>
                  <SummaryModal
                    isOpen={summaryModalIsOpen}
                    heading={t('newUser.summaryStep.modal.heading')}
                    onOkButtonClicked={handleSummaryModalSubmit}
                    onCloseButtonClicked={closeSummaryModal}
                    onCancelButtonClicked={closeSummaryModal}
                    okButtonLabel={t('newUser.summaryStep.modal.ok')}
                    cancelButtonLabel={t('newUser.summaryStep.modal.cancel')}
                  />
                  <SummaryStep push={push} currentStep={currentStep} isError={error} errorMessage={`${errorMessage}`} />
                </>
              )
            }
          />
        </Switch>
      </WizardView>
    );
  }
};

const actionCreators: DispatchProps = {
  push: RouterActions.push,
};

const mapStateToProps = (state: State): IStateProps => {
  let loadingClientsData = true;

  if (
    state.clients.groups.data.allIds.length !== 0 &&
    state.clients.brands.data.allIds.length !== 0 &&
    state.clients.sites.data.allIds.length !== 0
  ) {
    loadingClientsData = false;
  }

  const groups: IGroups = {
    allIds: state.clients.groups.data.allIds,
    byId: state.clients.groups.data.byId,
  };

  const brands: IBrands = {
    allIds: state.clients.brands.data.allIds,
    byId: state.clients.brands.data.byId,
  };

  const sites: ISites = {
    allIds: state.clients.sites.data.allIds,
    byId: state.clients.sites.data.byId,
  };

  const { pathname } = state.router.location;
  const currentStepName = RouterSelectors.getCurrentUserWizardStep(state);
  const currentStep = stepRoutes.indexOf(`/${currentStepName}`) + 1;

  return {
    loadingClientsData,
    clients: { groups, brands, sites },
    currentStep,
    pathname,
  };
};

export default connect(mapStateToProps, actionCreators)(NewUserWizard);
